interface GradientColorGroup {
  200: string;
  300: string;
  500: string;
  700: string;
  900: string;
  950: string;
}

interface DataVisualizationColors {
  blue: GradientColorGroup;
  moss: GradientColorGroup;
  orangeDark: GradientColorGroup;
  pink: GradientColorGroup;
  purple: GradientColorGroup;
  grayTrue: GradientColorGroup;
}

export const DATA_VISUALIZATION_COLORS: DataVisualizationColors = {
  blue: {
    200: "#b2ddff",
    300: "#84caff",
    500: "#2e90fa",
    700: "#175cd3",
    900: "#194185",
    950: "#102a56",
  },
  moss: {
    200: "#ceeab0",
    300: "#acdc79",
    500: "#669f2a",
    700: "#3f621a",
    900: "#2b4212",
    950: "#1a280b",
  },
  orangeDark: {
    200: "#ffd6ae",
    300: "#ff9c66",
    500: "#ff4405",
    700: "#bc1b06",
    900: "#771a0d",
    950: "#57130a",
  },
  pink: {
    200: "#fcceee",
    300: "#faa7e0",
    500: "#ee46bc",
    700: "#c11574",
    900: "#851651",
    950: "#4e0d30",
  },
  purple: {
    200: "#c5bbf7",
    300: "#a393f2",
    500: "#5f45e2",
    700: "#3e28ad",
    900: "#1b0d63",
    950: "#11064a",
  },
  grayTrue: {
    200: "#e5e5e5",
    300: "#d6d6d6",
    500: "#737373",
    700: "#424242",
    900: "#141414",
    950: "#0f0f0f",
  },
};

// Decided by @bcarper + @Jordyn + the UX team
// Talk to a UX designer before changing
// Order of these colors is significant
const SINGLE_GROUPING_DATA_VISUALIZATION_COLORS = [
  DATA_VISUALIZATION_COLORS.orangeDark[500],
  DATA_VISUALIZATION_COLORS.orangeDark[300],
  DATA_VISUALIZATION_COLORS.orangeDark[200],
  DATA_VISUALIZATION_COLORS.orangeDark[700],
  DATA_VISUALIZATION_COLORS.orangeDark[900],
  DATA_VISUALIZATION_COLORS.orangeDark[950],
  DATA_VISUALIZATION_COLORS.grayTrue[950],
  DATA_VISUALIZATION_COLORS.grayTrue[900],
  DATA_VISUALIZATION_COLORS.grayTrue[700],
  DATA_VISUALIZATION_COLORS.grayTrue[200],
  DATA_VISUALIZATION_COLORS.grayTrue[300],
  DATA_VISUALIZATION_COLORS.grayTrue[500],
  DATA_VISUALIZATION_COLORS.blue[500],
  DATA_VISUALIZATION_COLORS.blue[300],
  DATA_VISUALIZATION_COLORS.blue[200],
  DATA_VISUALIZATION_COLORS.blue[700],
  DATA_VISUALIZATION_COLORS.blue[900],
  DATA_VISUALIZATION_COLORS.blue[950],
  DATA_VISUALIZATION_COLORS.purple[950],
  DATA_VISUALIZATION_COLORS.purple[900],
  DATA_VISUALIZATION_COLORS.purple[700],
  DATA_VISUALIZATION_COLORS.purple[200],
  DATA_VISUALIZATION_COLORS.purple[300],
  DATA_VISUALIZATION_COLORS.purple[500],
  DATA_VISUALIZATION_COLORS.pink[500],
  DATA_VISUALIZATION_COLORS.pink[300],
  DATA_VISUALIZATION_COLORS.pink[200],
  DATA_VISUALIZATION_COLORS.pink[700],
  DATA_VISUALIZATION_COLORS.pink[900],
  DATA_VISUALIZATION_COLORS.pink[950],
  DATA_VISUALIZATION_COLORS.moss[950],
  DATA_VISUALIZATION_COLORS.moss[900],
  DATA_VISUALIZATION_COLORS.moss[700],
  DATA_VISUALIZATION_COLORS.moss[200],
  DATA_VISUALIZATION_COLORS.moss[300],
  DATA_VISUALIZATION_COLORS.moss[500],
];

// order of colors is significant
const SINGLE_GROUPING_DARK_TO_LIGHT_VISUALIZATION_COLORS = [
  DATA_VISUALIZATION_COLORS.orangeDark[950],
  DATA_VISUALIZATION_COLORS.orangeDark[700],
  DATA_VISUALIZATION_COLORS.orangeDark[500],
  DATA_VISUALIZATION_COLORS.orangeDark[300],
  DATA_VISUALIZATION_COLORS.orangeDark[200],
  DATA_VISUALIZATION_COLORS.blue[950],
  DATA_VISUALIZATION_COLORS.blue[700],
  DATA_VISUALIZATION_COLORS.blue[500],
  DATA_VISUALIZATION_COLORS.blue[300],
  DATA_VISUALIZATION_COLORS.blue[200],
  DATA_VISUALIZATION_COLORS.purple[950],
  DATA_VISUALIZATION_COLORS.purple[700],
  DATA_VISUALIZATION_COLORS.purple[500],
  DATA_VISUALIZATION_COLORS.purple[300],
  DATA_VISUALIZATION_COLORS.purple[200],
  DATA_VISUALIZATION_COLORS.pink[700],
  DATA_VISUALIZATION_COLORS.pink[500],
  DATA_VISUALIZATION_COLORS.pink[300],
  DATA_VISUALIZATION_COLORS.pink[200],
  DATA_VISUALIZATION_COLORS.moss[950],
  DATA_VISUALIZATION_COLORS.moss[700],
  DATA_VISUALIZATION_COLORS.moss[500],
  DATA_VISUALIZATION_COLORS.moss[300],
  DATA_VISUALIZATION_COLORS.moss[200],
];

export function getSingleGroupingDarkToLightDataVisualizationColors(
  n: number,
): string[] {
  if (n <= 0) {
    return [];
  } else {
    // cycle through expanded palette
    const colors = [];
    for (let i = 0; i < n; i++) {
      colors.push(
        SINGLE_GROUPING_DARK_TO_LIGHT_VISUALIZATION_COLORS[
          i % SINGLE_GROUPING_DARK_TO_LIGHT_VISUALIZATION_COLORS.length
        ],
      );
    }
    return colors;
  }
}

export function getSingleGroupingDataVisualizationColors(n: number): string[] {
  if (n <= 0) {
    return [];
  } else {
    // cycle through expanded palette
    const colors = [];
    for (let i = 0; i < n; i++) {
      colors.push(
        SINGLE_GROUPING_DATA_VISUALIZATION_COLORS[
          i % SINGLE_GROUPING_DATA_VISUALIZATION_COLORS.length
        ],
      );
    }
    return colors;
  }
}

export function getSingleGroupingDataVisualizationNthColor(n: number): string {
  return SINGLE_GROUPING_DATA_VISUALIZATION_COLORS[
    n % SINGLE_GROUPING_DATA_VISUALIZATION_COLORS.length
  ];
}

function getColorsForGrouping(
  gradientColorGroup: keyof DataVisualizationColors,
  n: number,
): string[] {
  if (n <= 0) {
    return [];
  } else {
    const allColorsInGradientColorGroup = [
      DATA_VISUALIZATION_COLORS[gradientColorGroup][500],
      DATA_VISUALIZATION_COLORS[gradientColorGroup][300],
      DATA_VISUALIZATION_COLORS[gradientColorGroup][200],
      DATA_VISUALIZATION_COLORS[gradientColorGroup][700],
      DATA_VISUALIZATION_COLORS[gradientColorGroup][900],
      DATA_VISUALIZATION_COLORS[gradientColorGroup][950],
    ];
    const colors = [];
    for (let i = 0; i < n; i++) {
      colors.push(
        allColorsInGradientColorGroup[i % allColorsInGradientColorGroup.length],
      );
    }
    return colors;
  }
}

/**
 * Example usage: Imagine you need to display a line chart with the following
 * 4 data series:
 * - AI (This month)
 * - AI (Last month)
 * - Human (This month)
 * - Human (Last month)
 *
 * Notice how the data series naturally fall into 2 groups/categories/clusters
 * of data. In these cases, it makes sense to group the related series with
 * colors from the same color gradient.
 *
 * @param groupings The number of groups of your data. In the example above,
 * these are "AI" and "Human"
 * @param itemsPerGroup The number of metrics in each group. In the example
 * above, these are "This month" and "Last month"
 * @returns
 */
export function getMultipleGroupingDataVisualizationColors(
  groupings: number,
  itemsPerGroup: number,
): string[][] {
  const orderedGradientColorGroups: (keyof DataVisualizationColors)[] = [
    "orangeDark",
    "grayTrue",
    "blue",
    "purple",
    "pink",
    "moss",
  ];
  const colorsInGroupings = [];
  for (let i = 0; i < groupings; i++) {
    colorsInGroupings.push(
      getColorsForGrouping(
        orderedGradientColorGroups[i % orderedGradientColorGroups.length],
        itemsPerGroup,
      ),
    );
  }
  return colorsInGroupings;
}

export function hexToRGBA(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
